import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "@/App";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useTheme } from "@/contexts/ThemeContext";
import DashboardLayout from "../components/DashboardLayout";
import { Bot, TrendingUp, TrendingDown, AlertTriangle, XCircle, Gem, DollarSign, Zap, Info } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const PRESETS = [
  { label: "7 días", days: 7 },
  { label: "15 días", days: 15 },
  { label: "30 días", days: 30 },
  { label: "90 días", days: 90 },
];

function chileDate(d) { const c = new Date(d); c.setHours(c.getHours() - 4); return c.toISOString().slice(0,10); }
function getDateRange(days) {
  const to = new Date();
  const from = new Date();
  from.setDate(from.getDate() - days);
  return { from: chileDate(from), to: chileDate(to) };
}

export default function KeywordIntelligence() {
  const { token } = useContext(AuthContext);
  const { clientId } = useWorkspace();
  const theme = useTheme();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDays, setSelectedDays] = useState(30);

  useEffect(() => { if (clientId) fetchAnalysis(); }, [clientId, selectedDays]);

  const fetchAnalysis = async () => {
    setLoading(true); setError(null); setData(null);
    try {
      const { from, to } = getDateRange(selectedDays);
      const r = await axios.get(`${API}/google-ads/keyword-intelligence`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { client_id: clientId, date_from: from, date_to: to }
      });
      setData(r.data);
    } catch (e) { setError("Error al cargar análisis."); }
    finally { setLoading(false); }
  };

  const fmt = (n) => typeof n === "number" ? n.toLocaleString("es-CL", { maximumFractionDigits: 0 }) : n;
  const fmtDec = (n) => typeof n === "number" ? n.toLocaleString("es-CL", { maximumFractionDigits: 2 }) : n;

  const analysis = data?.analysis;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Keyword Intelligence</h1>
            <p className={`text-sm ${theme.textSecondary} mt-1`}>Detectamos exactamente qué keywords hacen dinero y cuáles lo pierden</p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {PRESETS.map(p => (
              <button key={p.days} onClick={() => setSelectedDays(p.days)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${selectedDays === p.days ? "bg-blue-600 text-white" : `${theme.glassCard} ${theme.textSecondary}`}`}>
                {p.label}
              </button>
            ))}
          </div>
        </div>

        {!clientId && (
          <div className={`${theme.glassCard} rounded-xl p-8 text-center`}>
            <p className={theme.textSecondary}>Selecciona un cliente para ver el análisis.</p>
          </div>
        )}

        {error && <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-4"><p className="text-red-600 text-sm">{error}</p></div>}

        {loading && (
          <div className={`${theme.glassCard} rounded-xl p-12 text-center`}>
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
              <Bot className="w-8 h-8 text-blue-500" />
            </div>
            <p className={`font-semibold ${theme.text}`}>Analizando keywords con IA...</p>
            <p className={`text-sm ${theme.textSecondary} mt-1`}>Identificando money makers, wasters y oportunidades ocultas</p>
          </div>
        )}

        {data && analysis && (
          <>
            {/* Data type notice */}
            {data.data_type === "campaigns" && (
              <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-4 mb-5">
                <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-blue-800">Análisis basado en campañas</p>
                  <p className="text-xs text-blue-600 mt-0.5">Con Standard Access de Google Ads activado, este análisis usará keywords individuales para máxima precisión.</p>
                </div>
              </div>
            )}

            {/* KPI row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { label: "Gasto total", value: `$${fmt(data.total_spent)}`, color: "text-gray-700" },
                { label: "Conversiones", value: fmtDec(data.total_conversions), color: "text-green-600" },
                { label: "Gasto ineficiente", value: `$${fmt(analysis.total_waste)}`, color: "text-red-500" },
                { label: "Oportunidad estimada", value: `$${fmt(analysis.growth_opportunity)}`, color: "text-indigo-600" },
              ].map((k, i) => (
                <div key={i} className={`${theme.glassCard} rounded-xl p-4`}>
                  <p className={`text-xs ${theme.textSecondary} mb-1`}>{k.label}</p>
                  <p className={`text-xl font-bold ${k.color}`}>{k.value}</p>
                </div>
              ))}
            </div>

            {/* Executive summary */}
            <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-xl p-5 text-white mb-6">
              <div className="flex items-center gap-2 mb-2"><Bot className="w-5 h-5" /><span className="font-semibold">Resumen ejecutivo</span></div>
              <p className="text-blue-50 text-sm leading-relaxed">{analysis.executive_summary}</p>
              {analysis.pareto_names?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  <span className="text-xs text-blue-200">🎯 Pareto (80% conversiones):</span>
                  {analysis.pareto_names.map((n, i) => (
                    <span key={i} className="text-xs bg-white/20 rounded-full px-2 py-0.5">{n}</span>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Money Makers */}
              {analysis.money_makers?.length > 0 && (
                <div className={`${theme.glassCard} rounded-xl p-5`}>
                  <div className="flex items-center gap-2 mb-4">
                    <DollarSign className="w-5 h-5 text-green-500" />
                    <h3 className={`font-semibold ${theme.text}`}>🔥 Money Makers</h3>
                    <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full ml-auto">ESCALAR</span>
                  </div>
                  <div className="space-y-3">
                    {analysis.money_makers.map((k, i) => (
                      <div key={i} className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`font-medium text-sm ${theme.text}`}>{k.name}</span>
                          <span className="text-xs text-green-600 font-semibold">${fmt(k.spent)}</span>
                        </div>
                        <p className="text-xs text-green-700 dark:text-green-400 mb-2">{k.reason}</p>
                        <div className="flex gap-3 text-xs text-green-600">
                          {k.conversions > 0 && <span>Conv: {fmtDec(k.conversions)}</span>}
                          {k.cpa > 0 && <span>CPA: ${fmt(k.cpa)}</span>}
                          {k.ctr > 0 && <span>CTR: {fmtDec(k.ctr)}%</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Money Wasters */}
              {analysis.money_wasters?.length > 0 && (
                <div className={`${theme.glassCard} rounded-xl p-5`}>
                  <div className="flex items-center gap-2 mb-4">
                    <XCircle className="w-5 h-5 text-red-500" />
                    <h3 className={`font-semibold ${theme.text}`}>🚨 Money Wasters</h3>
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full ml-auto">PAUSAR</span>
                  </div>
                  <div className="space-y-3">
                    {analysis.money_wasters.map((k, i) => (
                      <div key={i} className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`font-medium text-sm ${theme.text}`}>{k.name}</span>
                          <span className="text-xs text-red-600 font-semibold">-${fmt(k.waste || k.spent)}</span>
                        </div>
                        <p className="text-xs text-red-600 dark:text-red-400 mb-1">{k.action}</p>
                        <div className="flex gap-3 text-xs text-red-500">
                          <span>Gasto: ${fmt(k.spent)}</span>
                          <span>Conv: {fmtDec(k.conversions)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Hidden Gems */}
              {analysis.hidden_gems?.length > 0 && (
                <div className={`${theme.glassCard} rounded-xl p-5`}>
                  <div className="flex items-center gap-2 mb-4">
                    <Gem className="w-5 h-5 text-purple-500" />
                    <h3 className={`font-semibold ${theme.text}`}>💎 Hidden Gems</h3>
                  </div>
                  <div className="space-y-3">
                    {analysis.hidden_gems.map((k, i) => (
                      <div key={i} className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3">
                        <span className={`font-medium text-sm ${theme.text} block mb-1`}>{k.name}</span>
                        <p className="text-xs text-purple-700 dark:text-purple-400 mb-1">{k.action}</p>
                        <div className="flex gap-3 text-xs text-purple-600">
                          {k.ctr > 0 && <span>CTR: {fmtDec(k.ctr)}%</span>}
                          {k.impressions > 0 && <span>Imp: {fmt(k.impressions)}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Trending */}
              {analysis.trending?.length > 0 && (
                <div className={`${theme.glassCard} rounded-xl p-5`}>
                  <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="w-5 h-5 text-blue-500" />
                    <h3 className={`font-semibold ${theme.text}`}>📈 Trending — Escalar YA</h3>
                  </div>
                  <div className="space-y-3">
                    {analysis.trending.map((k, i) => (
                      <div key={i} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
                        <span className={`font-medium text-sm ${theme.text} block mb-1`}>{k.name}</span>
                        <p className="text-xs text-blue-700 dark:text-blue-400">{k.reason}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Declining */}
            {analysis.declining?.length > 0 && (
              <div className={`${theme.glassCard} rounded-xl p-5 mb-4`}>
                <div className="flex items-center gap-2 mb-4">
                  <TrendingDown className="w-5 h-5 text-orange-500" />
                  <h3 className={`font-semibold ${theme.text}`}>📉 Declining — Revisar</h3>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {analysis.declining.map((k, i) => (
                    <div key={i} className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-3">
                      <span className={`font-medium text-sm ${theme.text} block mb-1`}>{k.name}</span>
                      <p className="text-xs text-orange-700 dark:text-orange-400">{k.reason}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Raw table */}
            <div className={`${theme.glassCard} rounded-xl overflow-hidden`}>
              <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className={`font-semibold ${theme.text}`}>Datos completos</h3>
                <p className={`text-xs ${theme.textSecondary}`}>{data.date_from} → {data.date_to} · {data.keywords?.length} elementos</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className={`${theme.textSecondary} text-xs uppercase bg-gray-50 dark:bg-gray-800/50`}>
                      <th className="text-left px-4 py-3">Keyword / Campaña</th>
                      <th className="text-right px-4 py-3">Gasto</th>
                      <th className="text-right px-4 py-3">Clics</th>
                      <th className="text-right px-4 py-3">CTR</th>
                      <th className="text-right px-4 py-3">Conv.</th>
                      <th className="text-right px-4 py-3">CPA</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.keywords?.map((k, i) => (
                      <tr key={i} className="border-t border-gray-50 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                        <td className={`px-4 py-3 font-medium ${theme.text}`}>{k.keyword}</td>
                        <td className={`px-4 py-3 text-right ${theme.text}`}>${fmt(k.spent)}</td>
                        <td className={`px-4 py-3 text-right ${theme.textSecondary}`}>{fmt(k.clicks)}</td>
                        <td className={`px-4 py-3 text-right font-medium ${k.ctr > 5 ? "text-green-600" : k.ctr < 2 ? "text-red-500" : "text-yellow-600"}`}>{fmtDec(k.ctr)}%</td>
                        <td className={`px-4 py-3 text-right ${theme.text}`}>{fmtDec(k.conversions)}</td>
                        <td className={`px-4 py-3 text-right font-medium ${k.cpa < 200 ? "text-green-600" : k.cpa > 800 ? "text-red-500" : "text-yellow-600"}`}>${fmt(k.cpa)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
